<div class="chatbot-dialog">
  <div class="chatbot-dialog-header">
    <div class="header-left">
      <mat-icon class="header-icon">smart_toy</mat-icon>
      <div>
        <h2 mat-dialog-title>Treatment Guide AI Assistant</h2>
        <span class="doc-title">{{ documentTitle }}</span>
      </div>
    </div>
    <div class="header-actions">
      <button
        mat-stroked-button
        class="new-chat-button"
        (click)="clearConversation()"
        [disabled]="messages.length === 0"
      >
        <mat-icon>add</mat-icon>
        <span>New Chat</span>
      </button>
      <button mat-icon-button matTooltip="Close" (click)="close()">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <!-- Hero: tool identity + scope -->
  <div class="chatbot-hero">
    <h3 class="hero-title">AI Clinical Reference Tool</h3>
    <p class="hero-subtitle">
      Answers are based only on this patient's Treatment Guide PDF. Always review responses
      before applying clinical judgment.
    </p>
  </div>

  <mat-dialog-content #chatContainer (scroll)="onChatScroll($event)">
    <!-- LANDING VIEW: shown before first message -->
    @if (!hasStartedChat) {
    <div class="landing-view">
      @if (isLoadingDocument) {
      <div class="loading-document">
        <mat-spinner diameter="24"></mat-spinner>
        <span>Loading treatment guide document...</span>
      </div>
      } @if (chatbotNotSupported && !isLoadingDocument) {
      <div class="image-not-supported">
        <mat-icon class="not-supported-icon">image_not_supported</mat-icon>
        <div class="not-supported-text">
          <strong>Limited AI support</strong>
          <span
            >Quick start questions have not been configured for this document. You can still
            try asking directly.</span
          >
        </div>
      </div>
      } @if (!isLoadingDocument && documentUrl) {
      <div class="landing-source-link">
        <a
          [href]="documentUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="view-source-btn"
        >
          <mat-icon class="source-icon">picture_as_pdf</mat-icon>
          <span>View Treatment Guide</span>
          <mat-icon class="link-icon">open_in_new</mat-icon>
        </a>
      </div>
      } @if (!chatbotNotSupported && !isLoadingDocument) {
      <div class="landing-presets">
        <div class="landing-preset-label">
          Suggested questions @if (isLoadingPresets) {
          <mat-spinner diameter="14" class="preset-spinner"></mat-spinner>
          }
        </div>
        @for (q of presetQuestions; track q) {
        <button
          (click)="sendMessage(q)"
          [disabled]="isLoading || isLoadingDocument || isLoadingPresets"
          class="landing-preset-item"
        >
          <span>{{ q }}</span>
          <mat-icon class="preset-arrow">chevron_right</mat-icon>
        </button>
        }
      </div>
      }
    </div>
    }

    <!-- CHAT VIEW: shown after first message -->
    @if (hasStartedChat) {
    <div class="chat-view">
      <div class="chat-container">
        @for (msg of messages; track msg) {
        <div class="message-row" [ngClass]="msg.role">
          <div class="message-bubble" [ngClass]="msg.role">
            @if (msg.role === 'assistant') {
            <div class="message-content" [innerHTML]="formatMessage(msg.content)"></div>
            } @if (msg.role !== 'assistant') {
            <div class="message-content">{{ msg.content }}</div>
            } @if (msg.source && documentUrl) {
            <a
              [href]="documentUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="source-link"
              [matTooltip]="msg.source"
            >
              <mat-icon class="link-icon">open_in_new</mat-icon>
              Source
            </a>
            }
            <div class="message-time">{{ msg.timestamp | date : 'HH:mm' }}</div>
          </div>
        </div>
        }
        <!-- Typing indicator -->
        @if (isLoading) {
        <div class="message-row assistant">
          <div class="message-bubble assistant typing">
            <div class="typing-dots"><span></span><span></span><span></span></div>
          </div>
        </div>
        }
        <!-- Follow-up questions after AI response -->
        @if (currentFollowUpQuestions.length > 0 && !isLoading) {
        <div class="follow-up-section">
          <span class="follow-up-label">You might also ask:</span>
          <div class="follow-up-chips">
            @for (q of currentFollowUpQuestions; track q) {
            <button (click)="sendMessage(q)" class="follow-up-chip">{{ q }}</button>
            }
          </div>
        </div>
        }
      </div>
      <!-- Scroll to bottom button -->
      @if (showScrollButton) {
      <div class="scroll-down-wrapper">
        <button
          class="scroll-down-btn"
          (click)="scrollToBottomClicked()"
          aria-label="Scroll to bottom"
        >
          <mat-icon>keyboard_arrow_down</mat-icon>
        </button>
      </div>
      }
    </div>
    }
  </mat-dialog-content>

  <!-- AI disclaimer notice (dismissable) -->
  @if (!noticeDismissed) {
  <div class="ai-notice">
    <mat-icon class="notice-icon">info_outline</mat-icon>
    <span class="notice-text">
      AI assistance supplements but does not replace clinical judgment. Please review every
      response.
    </span>
    <button
      mat-icon-button
      class="notice-close"
      (click)="dismissNotice()"
      aria-label="Dismiss"
    >
      <mat-icon>close</mat-icon>
    </button>
  </div>
  }

  <mat-dialog-actions class="input-area">
    <mat-form-field appearance="outline" class="chat-input-field">
      <input
        matInput
        [(ngModel)]="userInput"
        (keydown)="onKeyDown($event)"
        placeholder="Ask about the treatment guide..."
        [disabled]="isLoading || isLoadingDocument"
        maxlength="2000"
        autocomplete="off"
      />
    </mat-form-field>
    <button
      mat-icon-button
      color="primary"
      (click)="sendMessage()"
      [disabled]="!userInput?.trim() || isLoading || isLoadingDocument"
      class="send-button"
    >
      <mat-icon>send</mat-icon>
    </button>
  </mat-dialog-actions>
</div>
